import React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MAIN } from "../../RootRouter.config";
import { useSourceQuery } from "../../../queries/sources/sourceQuery";
import Loader from "components/other/Loader";
import { useViewerDecks, useViewerQuery } from "../../../queries/viewerQuery";
import { useGetState, useSetState } from "../../../components/ReduxProvider";
import Head from "../../../components/header/Head";
import { devicePaddings } from "../../../tools/device";
import { imgOGFormat } from "../../../tools/unsplash";
import { getSourceEditUrl, LANGNATIVEPARAM } from "../library/libraryUrls";
import CardNewButton from "../../main/card/CardNewButton";
import CardsMaxDialog, { useReachedMaximum } from "../../../components/dialogs/CardsMaxDialog";
import { useBurnWithSource, PICKED_FEW_CARDS } from "components/fire/xpHooks";
import { useEffectOnce } from "react-use";
import { CARDS_ACTION } from "../sourceEdit/SourceEdit";
import { FIRSTFEWHINT } from "root/tutorial/steps/FirstFewCards";
import { TUTORIALDONE } from "root/tutorial/steps/Verdict";
import SetCardsFilterDialog from "components/picker/SetCardsFilterDialog";
import SetAndArticleWrap from "./SetAndArticleWrap";
import VideoWrap from "./VideoWrap";
import OpenAssistantButton from "../../main/card/OpenAssistantButton";
import { Trans } from "react-i18next";
import { Stack } from "@mui/material";
import { useTheoryPickedLang, theoryGetContent } from "components/Theory/theoryTools";
import { TTheory } from "types/TheoryTypes";
import { langs } from "sharedJs__generated/langsList";
import { LEARN } from "root/main/MainRouter";
import GuestLoginPrompt from "./GuestLoginPrompt";
import ContentTutorialDialog from "./ContentTutorialDialog";

const { bottom } = devicePaddings();

const Source: React.FC = () => {
  const { uri } = useParams<{ uri: string }>();
  const { hintWasSeen, isSubscriber, viewer } = useViewerQuery();
  const { decks, deck } = useViewerDecks();
  const libTopic = useGetState("libTopic");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setLibTopic = useSetState("libTopic");
  const routeHistory = useGetState("routeHistory");
  const libTopicDeck = decks?.find((d) => d.front === libTopic);
  const ln = searchParams.get(LANGNATIVEPARAM);
  const source = useSourceQuery(uri || "", { ...libTopicDeck, ...(ln ? { back: ln } : {}) }, "store-or-network");
  const setDeckId = useSetState("deckId");
  const xp = useGetState("xp");
  const reachedMaximum = useReachedMaximum();

  const burnW = useBurnWithSource(source?.lang);
  useEffectOnce(() => burnW());

  const deckToBeSet =
    deck && source && deck.front !== source.lang && decks && decks.find((d) => d.front === source.lang);

  React.useEffect(() => {
    if (deckToBeSet) {
      setDeckId(deckToBeSet.id);
    }
    if (source?.lang && (!libTopic || source.lang !== libTopic)) {
      setLibTopic(source.lang);
    }
  }, [deckToBeSet, source, libTopic, setDeckId, setLibTopic]);

  useEffectOnce(() => {
    if (!hintWasSeen(FIRSTFEWHINT) && !hintWasSeen(TUTORIALDONE)) navigate({ hash: "noback" }); // block first native-back-button to force user pick 3 cards
  });

  const showFirstQuestDialog = !hintWasSeen(PICKED_FEW_CARDS) && xp.warn === "tooManyToLearn";

  // If showing first quest dialog and user came from /main/learn, redirect to /main (do not force quest)
  React.useEffect(() => {
    if (showFirstQuestDialog && routeHistory?.[0]?.pathname === LEARN.url()) {
      navigate(MAIN.url(), { replace: true });
    }
  }, [showFirstQuestDialog, routeHistory, navigate]);

  const firstTheory = source?.sCards?.find((c) => c.theory)?.theory as TTheory | undefined;
  const theoryPickedLang = useTheoryPickedLang(firstTheory);
  const theoryContent = firstTheory ? theoryGetContent(firstTheory, theoryPickedLang).replace(/\n/g, " ") : undefined;

  if (!source || !source.lang || deckToBeSet) return <Loader />;

  const keywords = [
    ...(source.lang && langs[source.lang] ? [langs[source.lang].native] : []),
    ...source.sCards.map((c) => (ln && c.back ? c.back?.value : c.front))
  ];

  const description = source.text || theoryContent || keywords.join(",") || undefined;

  return (
    <>
      <Head
        title={source.name + (source.kind === "set" ? ` - ${source.seo}` : "")}
        image={source?.image?.url && imgOGFormat(source?.image?.url)}
        description={description}
        keywords={keywords}
      />

      {source.kind === "video" ? <VideoWrap source={source} /> : <SetAndArticleWrap source={source} />}

      {viewer && (hintWasSeen(FIRSTFEWHINT) || hintWasSeen(TUTORIALDONE)) && (
        <>
          <OpenAssistantButton style={{ right: "46px", bottom: bottom + 5 }} />
          <CardNewButton
            onClickOverwrite={
              source.mine && source.kind === "set" ? () => navigate(getSourceEditUrl(source, CARDS_ACTION)) : undefined
            }
            style={{ bottom: bottom + 5, right: "5px" }}
          />
        </>
      )}

      {!viewer && <GuestLoginPrompt source={source} />}

      {viewer && source.kind !== "set" && <ContentTutorialDialog />}

      <SetCardsFilterDialog source={source} />

      {source.kind === "set" && showFirstQuestDialog && (
        <CardsMaxDialog
          hideClose
          showPermanently
          hideSubscribeButton
          title={<Trans>Quest completed!</Trans>}
          message={
            <Stack spacing={2}>
              <Trans parent={"span"}>
                Great, I see you've already added your cards. <strong>Let's learn them</strong>.
              </Trans>
            </Stack>
          }
        />
      )}

      {(source.kind === "article" || source.kind === "video") && reachedMaximum && !isSubscriber && (
        <CardsMaxDialog
          showPermanently
          subscribeButtonLabel={<Trans>Continue {source.kind === "article" ? "reading" : "watching"}</Trans>}
        />
      )}
    </>
  );
};

export default Source;
